import React, { useCallback, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import classnames from 'clsx';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpFromBracket } from '@fortawesome/sharp-solid-svg-icons/faArrowUpFromBracket';

import { Button } from 'ui-components/components/button';
import { FlexColumn, FlexRow, Gap } from 'ui-components/components/flex';
import { ProgressBar } from 'ui-components/components/progress-bar';
import { Text, TextType } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { validateFile } from '../../../../../components/file-upload-button/file-validation';
import { messages } from './messages';
import style from './document-drop-zone.module.less';

type DocumentDropZoneProps = {
  acceptedFileTypes: string[];
  isDisabled?: boolean;
  isUploading?: boolean;
  maxFileSizeMB?: number;
  onFileSelect: (file: File) => void;
  onUploadError: (errorMessage: string) => void;
  uploadProgress?: number;
};

/** Drag-and-drop file upload area with a Browse button. */
export const DocumentDropZone = ({
  acceptedFileTypes,
  isDisabled,
  isUploading,
  maxFileSizeMB,
  onFileSelect,
  onUploadError,
  uploadProgress = 0,
}: DocumentDropZoneProps) => {
  const intl = useIntl();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const processFile = useCallback(
    (file: File) => {
      const validation = validateFile(file, { acceptedFileTypes, maxFileSizeMB });
      if (validation.isValid) {
        onFileSelect(file);
        return;
      }
      if (validation.messageDescriptor) {
        onUploadError(intl.formatMessage(validation.messageDescriptor, validation.messageValues as Record<string, string>));
      }
    },
    [acceptedFileTypes, maxFileSizeMB, onFileSelect, onUploadError, intl],
  );

  const handleBrowseClick = useCallback(() => {
    if (!isDisabled) {
      fileInputRef.current?.click();
    }
  }, [isDisabled]);

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) processFile(file);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [processFile],
  );

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      setIsDragOver(false);
      if (isDisabled) return;
      const file = event.dataTransfer.files?.[0];
      processFile(file);
    },
    [isDisabled, processFile],
  );

  const formatsDisplay = acceptedFileTypes?.map((t) => t.replace('.', '')).join(', ');

  return (
    <div
      className={classnames(style['drop-zone'], {
        [style['drop-zone--drag-over']]: isDragOver && !isDisabled,
        [style['drop-zone--disabled']]: isDisabled,
      })}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {isUploading ? (
        <FlexColumn className={style['drop-zone-progress']} gap={Gap.Space8}>
          <FontAwesomeIcon className={style['drop-zone-icon']} icon={faArrowUpFromBracket} />
          <Text textType={TextType.Paragraph} value={messages.uploadingStatus} />
          <FlexRow className={style['drop-zone-progress-bar-row']} gap={Gap.Space8}>
            <Text className={style['drop-zone-progress-percentage']} textType={TextType.Caption} value={`${uploadProgress}%`} />
            <ProgressBar percentageComplete={uploadProgress} theme='primary' wrapperClassName={style['drop-zone-progress-bar']} />
          </FlexRow>
        </FlexColumn>
      ) : (
        <>
          <FontAwesomeIcon className={style['drop-zone-icon']} icon={faArrowUpFromBracket} />
          <Text textType={TextType.H4} value={messages.dropZoneTitle} />
          <FlexRow className={style['drop-zone-browse-row']} gap={Gap.Space8}>
            <Text textType={TextType.Paragraph} value={messages.dropZoneDragText} />
            <Button disabled={isDisabled} onClick={handleBrowseClick} theme={THEME_CONSTANTS.link}>
              <FormattedMessage {...messages.dropZoneBrowse} />
            </Button>
          </FlexRow>
          {formatsDisplay && (
            <Text
              className={style['drop-zone-formats']}
              textType={TextType.Caption}
              value={intl.formatMessage(messages.dropZoneFormats, { formats: formatsDisplay })}
            />
          )}
        </>
      )}
      <input
        ref={fileInputRef}
        accept={acceptedFileTypes?.join(',')}
        className={style['hidden-file-input']}
        onChange={handleFileChange}
        type='file'
      />
    </div>
  );
};
